/**
 * Remote access for the alias name reference listing, shared through
 * `useModel('aliasNameContext')` and consumed by `pages/v2/Reference/AliasNameList`.
 *
 * Deliberately cache-free, unlike `influencerContext`: this backs a curation
 * screen where the list must reflect what the server currently holds, and where
 * the same record is rarely requested twice.
 *
 * Every call takes `success`/`failed` callbacks instead of returning a value,
 * passes `skipErrorHandler: true` so a failure does not raise a global toast on
 * a screen that renders its own empty/error state, and treats a transport error
 * and a `success: false` body identically.
 *
 * One of four near-identical siblings (home / wiki / social / alias); a change
 * to the shape here almost certainly belongs in the other three too.
 */
// import { useState } from 'react';
import {
  getV2AliasNamesByInfluencersCount,
  getV2AliasNames,
  countV2AliasNames,
  markAliasNameFakeness,
} from '@/services/rest/api';

export default () => {
  const doGetAliasNamesByInfluencersCount = async (
    success: (socialPages: DATA.AliasName[]) => void,
    failed: () => void,
  ) => {
    try {
      const msg = await getV2AliasNamesByInfluencersCount({ skipErrorHandler: true });
      if (msg.success && msg.data) {
        success(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error fetching social pages:', error);
      failed();
    }
  };

  const doGetAliasNames = async (
    success: (socialPages: DATA.AliasName[]) => void,
    failed: () => void,
    isInvalid?: boolean,
    lastId?: string,
  ) => {
    try {
      const msg = await getV2AliasNames(isInvalid, lastId, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        success(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error fetching social pages:', error);
      failed();
    }
  };

  const doCountAliasNames = async (
    success: (count: number) => void,
    failed: () => void,
    isInvalid?: boolean,
  ) => {
    try {
      const msg = await countV2AliasNames(isInvalid, { skipErrorHandler: true });
      if (msg.success && typeof msg.data === 'number') {
        success(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error count social pages:', error);
      failed();
    }
  };

  const doMarkAliasNameFakeness = async (
    success: (obj: { id: string; isFake: boolean }) => void,
    failed: () => void,
    id: string,
    isFake: boolean,
  ) => {
    try {
      const msg = await markAliasNameFakeness(id, isFake, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        success(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error mark alias name fakeness:', error);
      failed();
    }
  };

  return {
    doGetAliasNamesByInfluencersCount,
    doGetAliasNames,
    doCountAliasNames,
    doMarkAliasNameFakeness,
  };
};
